import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AnalyticsCustomerDashboardData,
  AnalyticsCarrierDashboardData,
  EarningsPeriodBreakdown,
} from '@hero/api';
import { LoadMainIcon, VehicleMainIcon, OrderStatusDeliveredIcon, InfoIcon } from '@hero/web-icons';
import { AnalyticsCard, GrowthIndicator } from '../../../app/AnalyticsUI';
import type { AnalyticsCardOption } from '../../../app/AnalyticsUI';
import { Price } from '../../../app/Price';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/Tooltip';

type OverviewData = AnalyticsCustomerDashboardData | AnalyticsCarrierDashboardData;

export interface AnalyticsOverviewProps {
  data: OverviewData | undefined;
  displayForUser: 'customer' | 'carrier';
  isLoading: boolean;
}

/** Type guard to check if data has carrier earnings */
function hasEarnings(
  data: OverviewData,
  displayForUser: string,
): data is AnalyticsCarrierDashboardData {
  return displayForUser === 'carrier' && 'earnings' in data;
}

export const AnalyticsOverview: React.FC<AnalyticsOverviewProps> = ({
  data,
  displayForUser,
  isLoading,
}) => {
  const { t } = useTranslation(['m-analytics', 'c-dates']);

  // Default empty data
  const newOrders = data?.newOrders ?? { today: 0, thisWeek: 0, thisMonth: 0, growthPercentage: 0 };
  const activeOrders = data?.activeOrders ?? { total: 0, confirmed: 0, pending: 0 };
  const delivered = data?.delivered ?? { today: 0, thisWeek: 0, thisMonth: 0, growthPercentage: 0 };
  const canceledToday = data?.canceled?.today ?? 0;
  // `needsDriver` is a carrier-only field (the customer dashboard
  // doesn't ship it). Falls through to 0 for customer dashboards or
  // older backends that haven't shipped the field yet.
  const needsDriverCount =
    data && hasEarnings(data, displayForUser) ? data.needsDriver?.count ?? 0 : 0;

  // --- Card 1: New Orders ---
  const newOrdersOptions: AnalyticsCardOption[] = [
    { key: 'today', label: t('c-dates:relative_dates.today'), count: newOrders.today },
    {
      key: 'thisWeek',
      label: t('c-dates:relative_dates.this_week'),
      count: newOrders.thisWeek,
    },
    { key: 'thisMonth', label: t('c-dates:relative_dates.this_month'), count: newOrders.thisMonth },
  ];

  // --- Card 2: Active Orders ---
  // For carrier dashboards we add a "Needs Driver" tab — orders with no
  // `Order.driver` party yet. It sits next to "Pending"
  // (same filter source on the overview payload) so the user can see
  // what's waiting for a driver pick in one place.
  const activeOrdersOptions: AnalyticsCardOption[] = [
    { key: 'confirmed', label: t('m-analytics:overview.confirmed'), count: activeOrders.confirmed },
    {
      key: 'pending',
      label: t('m-analytics:overview.pending'),
      count: activeOrders.pending,
      description:
        activeOrders.pending > 0
          ? t('m-analytics:overview.pending_description')
          : t('m-analytics:overview.no_pending'),
    },
    ...(displayForUser === 'carrier'
      ? [
          {
            key: 'needs_driver',
            label: t('m-analytics:overview.needs_driver'),
            count: needsDriverCount,
            description: t('m-analytics:overview.needs_driver_description'),
          },
        ]
      : []),
    {
      key: 'canceled',
      label: t('m-analytics:overview.canceled'),
      count: canceledToday,
      description: t('m-analytics:overview.canceled_description'),
    },
  ];

  // --- Card 3: Delivered ---
  const deliveredOptions: AnalyticsCardOption[] = [
    { key: 'today', label: t('c-dates:relative_dates.today'), count: delivered.today },
    {
      key: 'thisWeek',
      label: t('c-dates:relative_dates.this_week'),
      count: delivered.thisWeek,
    },
    { key: 'thisMonth', label: t('c-dates:relative_dates.this_month'), count: delivered.thisMonth },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:grid-cols-3">
      {/* Card 1: New Orders */}
      <AnalyticsCard
        title={t('m-analytics:overview.new_orders')}
        options={newOrdersOptions}
        isLoading={isLoading}
        growthValue={newOrders.growthPercentage}
        growthLabel={t('c-dates:growth.vs_last_week')}
        icon={<LoadMainIcon className="w-5 h-5 text-blue-600" />}
        iconBg="bg-blue-50"
      />

      {/* Card 2: Active Orders */}
      <AnalyticsCard
        title={t('m-analytics:overview.active_orders')}
        options={activeOrdersOptions}
        isLoading={isLoading}
        icon={<VehicleMainIcon className="w-5 h-5 text-amber-600" />}
        iconBg="bg-amber-50"
      />

      {/* Card 3: Delivered */}
      <AnalyticsCard
        title={t('m-analytics:overview.delivered')}
        options={deliveredOptions}
        isLoading={isLoading}
        icon={<OrderStatusDeliveredIcon className="w-5 h-5 text-green-600" />}
        growthValue={delivered.growthPercentage}
        growthLabel={t('c-dates:growth.vs_last_week')}
        iconBg="bg-green-50"
        footer={
          data && hasEarnings(data, displayForUser)
            ? activeKey => {
                // Reuse the Delivered card's active tab — when the user
                // flips to "today" / "this week", the earnings figure
                // flips too, mirroring the mobile home page rather than
                // pinning the dashboard to "this month".
                const period = activeKey === 'today' || activeKey === 'thisWeek' ? activeKey : 'thisMonth';
                const periodLabelKey =
                  period === 'today'
                    ? 'm-analytics:overview.earnings_today'
                    : period === 'thisWeek'
                      ? 'm-analytics:overview.earnings_this_week'
                      : 'm-analytics:overview.earnings_this_month';
                const breakdown: EarningsPeriodBreakdown = data.earnings[period];
                return (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">{t(periodLabelKey)}</span>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="text-gray-400 hover:text-gray-600">
                                <InfoIcon className="size-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="space-y-1 p-2.5">
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-xs text-gray-400">{t('m-analytics:overview.earnings_before_tax')}</span>
                                <Price amount={breakdown.gross} currency={data.currency} className="text-xs font-medium" />
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-xs text-gray-400">{t('m-analytics:overview.earnings_with_tax')}</span>
                                <Price amount={breakdown.total} currency={data.currency} className="text-xs font-medium" />
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="flex items-center gap-2">
                        <Price amount={breakdown.gross} currency={data.currency} className="text-sm font-semibold text-black" />
                        {period === 'thisMonth' && (
                          <GrowthIndicator
                            growth={data.earnings.growthPercentage}
                            growthLabel={t('c-dates:growth.vs_last_month')}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
            : undefined
        }
      />
    </div>
  );
};
